import { create } from "zustand";
import { devtools } from "zustand/middleware";

// Per-slide annotations are serialized as the SVG markup that lives
// inside drauu's <svg> element (the string returned by `drauu.el.innerHTML`
// or equivalently `drauu.dump()`). BroadcastChannel-safe (just a string)
// and trivially loadable on a peer surface via `drauu.load(svg)`.
export type AnnotationSnapshot = string;

// All tool ids the UI knows about. Tools split into three groups by
// implementation:
//   - drauu-native (draw, highlight, eraser, laser, arrow) — handled
//     via drauu.mode + brush config in annotation-overlay's
//     `brushConfigFor`. Laser is "draw mode + per-stroke fade".
//   - custom-creation (text, note) — drauu can't author these; the
//     overlay intercepts pointerdown when active, opens an HTML
//     <input>, then appends an SVG <text> or <g><rect/><text/></g> to
//     drauu's SVG. drauu.el.innerHTML serialization picks them up.
//   - custom-manipulation (hand) — moves committed text/note elements.
//     Intercepts pointerdown, grabs the closest tagged ancestor, and
//     updates its position via x/y attrs (text) or transform (note)
//     while broadcasting over BC.
export type AnnotationTool =
  | "draw"
  | "highlight"
  | "eraser"
  | "laser"
  | "arrow"
  | "text"
  | "note"
  | "hand"
  | null;

// 10-color palette: rainbow (red → violet) followed by neutrals
// (pink as accent, then black/gray/white). Order matters — this is the
// left-to-right order rendered in the toolbar's color popover. Color
// names are arbitrary identifiers; the AnnotationOverlay passes the
// mapped hex value directly to drauu's brush config.
export const ANNOTATION_COLORS = [
  "red",
  "orange",
  "yellow",
  "green",
  "blue",
  "violet",
  "pink",
  "black",
  "gray",
  "white",
] as const;

export type AnnotationColor = (typeof ANNOTATION_COLORS)[number];

// Display hex for each color name — used by the toolbar to render
// swatch backgrounds AND by the overlay's brushConfigFor() to set
// drauu's stroke color (plus an alpha-mixed variant for highlighter).
export const ANNOTATION_COLOR_HEX: Record<AnnotationColor, string> = {
  red: "#e03131",
  orange: "#f76707",
  yellow: "#f1ac4b",
  green: "#37b24d",
  blue: "#4263eb",
  violet: "#7048e8",
  pink: "#e64980",
  black: "#1d1d1d",
  gray: "#6b7280",
  white: "#f8f9fa",
};

// Auto-contrast text color for the Note tool — when the user picks a
// background color, the text on top picks white for dark colors and
// dark for light colors so it stays legible without computing
// luminance at runtime.
export const NOTE_TEXT_HEX: Record<AnnotationColor, string> = {
  red: "#ffffff",
  orange: "#ffffff",
  yellow: "#1d1d1d",
  green: "#ffffff",
  blue: "#ffffff",
  violet: "#ffffff",
  pink: "#ffffff",
  black: "#ffffff",
  gray: "#ffffff",
  white: "#1d1d1d",
};

// Brush size selector — multiplies each tool's base size so the user
// can scale the whole annotation system without per-tool tuning.
// Order matters: this is the left-to-right order rendered in the toolbar.
export const BRUSH_SIZES = ["S", "M", "L", "XL"] as const;
export type BrushSize = (typeof BRUSH_SIZES)[number];

export const BRUSH_SIZE_MULTIPLIER: Record<BrushSize, number> = {
  S: 0.3,
  M: 0.7,
  L: 1.2,
  XL: 2,
};

interface AnnotationState {
  // null = toolbar closed / no tool selected → overlay should be
  // pointer-events: none so input passes through to the slide iframe.
  activeTool: AnnotationTool;
  color: AnnotationColor;
  brushSize: BrushSize;
  // Per-slide TLDraw snapshot. Session-ephemeral — wiped on End
  // Presentation via clearAll(). Keyed by slideIndex (not slide.id) because
  // BroadcastChannel peers may not have an identical slides array in
  // flight; index is the deterministic shared key.
  snapshotsBySlide: Record<number, AnnotationSnapshot>;

  setTool: (tool: AnnotationTool) => void;
  setColor: (color: AnnotationColor) => void;
  setBrushSize: (size: BrushSize) => void;
  saveSlideSnapshot: (slideIndex: number, snapshot: AnnotationSnapshot) => void;
  loadSlideSnapshot: (slideIndex: number) => AnnotationSnapshot | undefined;
  clearSlide: (slideIndex: number) => void;
  clearAll: () => void;
}

export const useAnnotationStore = create<AnnotationState>()(
  devtools(
    (set, get) => ({
      activeTool: null,
      color: "red",
      brushSize: "M",
      snapshotsBySlide: {},

      setTool: (tool) => set({ activeTool: tool }),
      setColor: (color) => set({ color }),
      setBrushSize: (size) => set({ brushSize: size }),
      saveSlideSnapshot: (slideIndex, snapshot) =>
        set((s) => ({
          snapshotsBySlide: {
            ...s.snapshotsBySlide,
            [slideIndex]: snapshot,
          },
        })),
      loadSlideSnapshot: (slideIndex) => get().snapshotsBySlide[slideIndex],
      clearSlide: (slideIndex) =>
        set((s) => {
          if (!(slideIndex in s.snapshotsBySlide)) return s;
          const next = { ...s.snapshotsBySlide };
          delete next[slideIndex];
          return { snapshotsBySlide: next };
        }),
      clearAll: () => set({ snapshotsBySlide: {} }),
    }),
    { name: "annotation-store" },
  ),
);
